import React from 'react';
import {
    Container,
    Box,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    useTheme,
    alpha,
    Paper
} from '@mui/material';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';

const Faq = () => {
    const [expandedPanel, setExpandedPanel] = React.useState(false);
    const theme = useTheme();

    const handleChange = (panel) => (event, newExpanded) => {
        setExpandedPanel(newExpanded ? panel : false);
    };

    const questionsAndAnswers = [
        {
            question: "What is Market Rodeo?",
            answer: "Market Rodeo is a stock market research platform built for retail investors. It gives you access to market data, advanced stock screeners, portfolio tracking, AI-powered research through RodeoAI, and much more. Whether you're just starting out or actively managing a portfolio, Market Rodeo has the tools to help you make better investment decisions."
        },
        {
            question: "Do you offer a free plan?",
            answer: "Yes, we offer a limited free plan that allows you to try Market Rodeo before committing to a subscription. We also offer Basic and Premium plans, which include additional features and advanced analysis tools."
        },
        {
            question: "Which plan should I choose?",
            answer: "Start with the free plan to explore the platform. If you need more data, alerts, and portfolios, the Basic plan is a great next step. The Premium plan is for power users who want full access to everything. Check the pricing section above to compare all features side by side."
        },
        {
            question: "Can I track cryptocurrency prices on Market Rodeo?",
            answer: "Yes. Market Rodeo supports real-time cryptocurrency tracking with price data, charts, and market analysis. You can add crypto to your watchlists alongside stocks and ETFs, giving you a complete view of all your assets in one place."
        },
        {
            question: "How do I subscribe to the premium plan?",
            answer: "Head to the Pricing section, choose the plan that fits your needs, and follow the checkout process. You can pay monthly or annually. Annual billing saves you 20% compared to monthly."
        },
        {
            question: "Can I cancel my subscription anytime?",
            answer: "Yes, you can cancel at any time from your account settings. Your access continues until the end of your current billing period. No cancellation fees, no hassle."
        },
        {
            question: "How can I reach out?",
            answer: "You can email us at support@marketrodeo.com or use the contact form on the website. We respond to every message and are happy to help with any questions, feature requests, or issues."
        }
    ];

    return (
        <Container maxWidth="lg">
            <Box
                sx={{
                    py: { xs: 4, md: 8 },
                    position: 'relative',
                }}
            >
                <Box
                    sx={{
                        position: 'relative',
                        textAlign: 'center',
                        mb: { xs: 3, md: 6 },
                    }}
                >
                    <QuestionAnswerIcon
                        sx={{
                            fontSize: 40,
                            color: theme.palette.primary.main,
                            mb: 2
                        }}
                    />
                    <Typography
                        variant="h3"
                        component="h2"
                        gutterBottom
                        sx={{
                            fontWeight: 700,
                            pb: 1,
                            background: theme.palette.mode === 'dark'
                                ? 'linear-gradient(to right, #fff 0%, #c7c7c7 100%)'
                                : 'linear-gradient(to right, #1976d2 0%, #1565c0 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                        }}
                    >
                        Questions? We got answers.
                    </Typography>
                </Box>

                <Paper
                    elevation={0}
                    sx={{
                        background: theme.palette.mode === 'dark'
                            ? alpha(theme.palette.background.paper, 0.8)
                            : theme.palette.background.paper,
                        borderRadius: 2,
                        overflow: 'hidden',
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    }}
                >
                    {questionsAndAnswers.map((qa, index) => (
                        <Accordion
                            key={index}
                            expanded={expandedPanel === `panel${index}`}
                            onChange={handleChange(`panel${index}`)}
                            elevation={0}
                            disableGutters
                            sx={{
                                background: 'transparent',
                                '&:before': {
                                    display: 'none',
                                },
                                '&:not(:last-child)': {
                                    borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                                },
                                '&:hover': {
                                    background: alpha(theme.palette.primary.main, 0.02),
                                }
                            }}
                        >
                            <AccordionSummary
                                expandIcon={
                                    expandedPanel === `panel${index}` ? (
                                        <CloseIcon
                                            sx={{
                                                color: theme.palette.primary.main,
                                            }}
                                        />
                                    ) : (
                                        <ExpandMoreIcon
                                            sx={{
                                                color: theme.palette.primary.main,
                                            }}
                                        />
                                    )
                                }
                                aria-controls={`panel${index}bh-content`}
                                id={`panel${index}bh-header`}
                                sx={{
                                    minHeight: { xs: 56, sm: 70, md: 80 },
                                    px: { xs: 2, sm: 3, md: 4 },
                                    '&:hover': {
                                        '& .MuiTypography-root': {
                                            color: theme.palette.primary.main,
                                        }
                                    }
                                }}
                            >
                                <Typography
                                    variant="h6"
                                    component="div"
                                    sx={{
                                        fontWeight: 600,
                                        fontSize: { xs: '0.9rem', sm: '1rem', md: '1.15rem' },
                                        transition: 'color 0.2s ease-in-out',
                                    }}
                                >
                                    {qa.question}
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ px: { xs: 2, sm: 3, md: 4 }, pb: { xs: 3, md: 4 } }}>
                                <Typography
                                    sx={{
                                        color: theme.palette.mode === 'dark'
                                            ? alpha('#fff', 0.7)
                                            : alpha('#000', 0.7),
                                        lineHeight: 1.7,
                                        fontSize: { xs: '0.825rem', md: '0.9rem' }
                                    }}
                                >
                                    {qa.answer}
                                </Typography>
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </Paper>
            </Box>
        </Container>
    );
}

export default Faq;